import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { StudioRecipe } from '@sr-test-wizard/shared';
import { studioRecipesApi } from '../../lib/studioRecipesApi';
import type { EnvLinkRow } from '../../lib/envLinksApi';

interface Props {
  recipe: StudioRecipe;
}

/**
 * Inline preview that calls `POST /api/studio-recipes/preview` with the
 * current form state plus a synthetic Game task, so the user sees exactly
 * what URL/guide Phase 2 would generate before saving. Stays empty until
 * the user clicks "Render preview" so we don't spam the server on every
 * keystroke.
 */
export function TestRenderPanel({ recipe }: Props) {
  const knownMarkets = useMemo(() => {
    const set = new Set<string>();
    Object.keys(recipe.marketCodeMap ?? {}).forEach((k) => set.add(k.toUpperCase()));
    Object.keys(recipe.perMarketOverrides ?? {}).forEach((k) => set.add(k.toUpperCase()));
    return Array.from(set).sort();
  }, [recipe]);

  const [jurisdiction, setJurisdiction] = useState(knownMarkets[0] ?? 'MGA');
  const [playUrl, setPlayUrl] = useState('');
  const [gameId, setGameId] = useState('');
  const [gameName, setGameName] = useState('');
  const [result, setResult] = useState<EnvLinkRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const row = await studioRecipesApi.preview({
        recipe,
        jurisdiction: jurisdiction || 'MGA',
        sampleGame: {
          market: jurisdiction || 'MGA',
          playUrl: playUrl || null,
          gameId: gameId || null,
          gameName: gameName || null,
        },
      });
      setResult(row);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <h3>Test render</h3>
      <p className="muted small" style={{ margin: '0 0 10px' }}>
        Try the recipe against a fake Game task to see the URL and guide it
        produces. Nothing is saved.
      </p>
      <div className="flex-col" style={{ gap: 8 }}>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <label className="muted small">Jurisdiction</label>
          <input
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value.toUpperCase())}
            placeholder="MGA"
            style={{ width: 120 }}
            list="known-markets"
          />
          <datalist id="known-markets">
            {knownMarkets.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <label className="muted small">Game ID</label>
          <input value={gameId} onChange={(e) => setGameId(e.target.value)} placeholder="salmon-king" />
          <label className="muted small">Game name</label>
          <input value={gameName} onChange={(e) => setGameName(e.target.value)} placeholder="Salmon King" />
        </div>
        <div className="row" style={{ gap: 6 }}>
          <label className="muted small" style={{ minWidth: 80 }}>
            Play URL
          </label>
          <input
            value={playUrl}
            onChange={(e) => setPlayUrl(e.target.value)}
            placeholder="https://lobby-api.example.com/launch/<slug>"
            style={{ flex: 1 }}
          />
        </div>
        <div className="row">
          <button type="button" className="btn small btn-primary" onClick={() => void run()} disabled={loading}>
            {loading ? 'Rendering...' : 'Render preview'}
          </button>
          {result && (
            <button type="button" className="btn small btn-ghost" onClick={() => setResult(null)}>
              Clear
            </button>
          )}
        </div>
        {error && <p style={{ color: 'var(--bad)', margin: 0 }}>{error}</p>}
        {result && (
          <div className="card" style={{ padding: 10 }}>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <span className="badge mono">{result.jurisdiction}</span>
              <span className={`badge small ${result.status === 'ok' ? 'good' : 'warn'}`}>
                {result.status}
              </span>
              {result.resolvedMarket && result.resolvedMarket !== result.jurisdiction && (
                <span className="badge small">resolved: {result.resolvedMarket}</span>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <code className="env-link-url-text" style={{ wordBreak: 'break-all' }}>
                {result.generatedUrl ?? '(no URL produced)'}
              </code>
            </div>
            {result.warnings.length > 0 && (
              <ul className="muted small" style={{ marginTop: 8, paddingLeft: 18 }}>
                {result.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
            {result.guideMd && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn small btn-ghost"
                  onClick={() => setShowGuide((s) => !s)}
                >
                  {showGuide ? 'Hide guide' : 'Show guide'}
                </button>
                {showGuide && (
                  <div className="env-link-guide" style={{ marginTop: 8 }}>
                    <ReactMarkdown>{result.guideMd}</ReactMarkdown>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
